/**
 * Google Workspace auth (AUTH_PROVIDER=google), ported from frontend/.
 *
 * Google Identity Services' implicit flow (a popup) returns an access token,
 * which is the API bearer token: the backend validates it against Google
 * (audience, verified email, Workspace domain) and reuses it for Google Drive.
 * Drive access is asked for at sign-in. There's no silent renewal, so the user
 * is signed out shortly before the token expires (about an hour).
 */

import { useToast } from "@design";
import { hasGrantedAllScopesGoogle, type TokenResponse, useGoogleLogin, useGoogleOAuth } from "@react-oauth/google";
import { hashKey, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, getCurrentUserInfoQueryKey, setTokenProvider } from "@/api";
import { formatList } from "@/i18n/format";
import { AuthContext, type AuthContextValue } from "../AuthContext";
import {
  clearToken,
  EXPIRY_MARGIN_MS,
  type GoogleToken,
  isUsable,
  loadToken,
  saveToken,
  toGoogleToken,
} from "./googleToken";

const API = "https://www.googleapis.com/auth";

/**
 * What each Google product the assistant reaches with the sign-in token needs (MHS-575: Docs, Sheets and Slides read
 * and write). One scope per product: the full scope covers reading, and asking for a readonly one too can make Google
 * report it unpredictably. Same set as frontend/. GIS adds "openid profile email" itself.
 */
export const GOOGLE_PRODUCT_SCOPES: Readonly<Record<string, readonly [string, ...string[]]>> = {
  "Google Drive": [`${API}/drive.readonly`],
  "Google Docs": [`${API}/documents`],
  "Google Sheets": [`${API}/spreadsheets`],
  "Google Slides": [`${API}/presentations`],
};

export const GOOGLE_SCOPES = Object.values(GOOGLE_PRODUCT_SCOPES).flat().join(" ");

/** The products whose access the user unticked on Google's consent screen. */
export function declinedProducts(response: TokenResponse): string[] {
  return Object.entries(GOOGLE_PRODUCT_SCOPES)
    .filter(([, scopes]) => !hasGrantedAllScopesGoogle(response, ...scopes))
    .map(([product]) => product);
}

export function GoogleAuthProvider({ children }: Readonly<{ children: ReactNode }>) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation("shell");
  const { scriptLoadedSuccessfully } = useGoogleOAuth();
  const [token, setToken] = useState<GoogleToken | null>(() => loadToken());
  // The token getter reads this ref, so a fresh sign-in is visible to API
  // calls before React re-renders.
  const tokenRef = useRef(token);

  const signOut = useCallback(() => {
    // A different account must never see the previous one's cached data.
    queryClient.clear();
    clearToken();
    tokenRef.current = null;
    setToken(null);
  }, [queryClient]);

  // Timers don't run while a laptop sleeps, so the expiry timer can fire late:
  // every token read and every return to the tab checks the clock too.
  const signOutIfExpired = useCallback(() => {
    if (tokenRef.current && !isUsable(tokenRef.current)) signOut();
  }, [signOut]);

  // Layout effect: registered before any child's passive effect starts a query.
  useLayoutEffect(() => {
    setTokenProvider(() => {
      if (isUsable(tokenRef.current)) return Promise.resolve(tokenRef.current.accessToken);
      queueMicrotask(signOutIfExpired);
      return Promise.resolve(null);
    });
    return () => setTokenProvider(null);
  }, [signOutIfExpired]);

  useEffect(() => {
    if (!token) return;
    const timer = setTimeout(signOut, Math.max(token.expiresAt - EXPIRY_MARGIN_MS - Date.now(), 0));
    globalThis.addEventListener("focus", signOutIfExpired);
    document.addEventListener("visibilitychange", signOutIfExpired);
    return () => {
      clearTimeout(timer);
      globalThis.removeEventListener("focus", signOutIfExpired);
      document.removeEventListener("visibilitychange", signOutIfExpired);
    };
  }, [token, signOut, signOutIfExpired]);

  // Google issues a token for any Google account; the backend rejects ones
  // outside the Workspace (or revoked). Sign out instead of showing a broken app.
  useEffect(() => {
    const meHash = hashKey(getCurrentUserInfoQueryKey());
    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== "updated" || event.action.type !== "error" || event.query.queryHash !== meHash) return;
      if (event.action.error instanceof ApiError && event.action.error.status === 401 && tokenRef.current) {
        signOut();
        toast.error(t("signIn.rejected"));
      }
    });
  }, [queryClient, signOut, toast, t]);

  const googleLogin = useGoogleLogin({
    flow: "implicit",
    scope: GOOGLE_SCOPES,
    onSuccess: (response) => {
      queryClient.clear();
      const next = toGoogleToken(response.access_token, response.expires_in);
      saveToken(next);
      tokenRef.current = next;
      setToken(next);
      // Google lets the user untick each product on the consent screen.
      const declined = declinedProducts(response);
      if (declined.length > 0) toast.warning(t("signIn.scopesDeclined", { products: formatList(declined) }));
    },
    onError: (error) => {
      console.warn("[auth] Google sign-in failed:", error.error, error.error_description);
      toast.error(t("signIn.failed"));
    },
    onNonOAuthError: (error) => {
      // Closing the popup is the user's choice; a blocked popup needs telling.
      if (error.type === "popup_failed_to_open") toast.error(t("signIn.popupBlocked"));
    },
  });

  const login = useCallback(() => {
    // Until Google's script loads (or if it's blocked), the popup can't open.
    if (scriptLoadedSuccessfully) googleLogin();
    else toast.error(t("signIn.unavailable"));
    return Promise.resolve();
  }, [scriptLoadedSuccessfully, googleLogin, toast, t]);

  const logout = useCallback(() => {
    // The implicit flow has no server-side session to end; dropping the token is enough.
    signOut();
    return Promise.resolve();
  }, [signOut]);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: token !== null,
      isLoading: false,
      // An access token carries no profile; the sidebar falls back to /auth/me.
      accountName: null,
      login,
      logout,
    }),
    [token, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
